import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, Inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { NgForm } from '@angular/forms';
import { escape } from 'lodash';
import { AnyComponentData, ConnectionSchemaField, Schema } from '../../package.models';
import { AppState } from '../../../store';
import { setComponentValidity, updateComponent, updateRawComponent } from '../../store/component.actions';
import { BaseForm, BaseFormInterface } from '../../../common/base/base-form.component';
import { NotifyService } from '../../../common/services/notify.service';
import { ComponentTypeItem } from '../../../constants/component_types';
import { getStep } from '../../../common/helper/get-step.helper';
import { Step } from '../../../common/components/xp-steps.component';
import { SelectPickerTypes } from '../../../common/components/forms/select-picker/select-picker-types.enum';
import { connectionIconUrlByType } from '../../../common/helper/connection-icon-url-by-type.helper';
import { AnyConnection } from '../../../connections/connection.models';
import { ComponentFormTagsService } from '../../../common/services/component-form-tags.service';
import { getConnectionSchema } from '../../../connections/store/connections.actions';
import { getDataForSchemaImporter } from '../../helpers/schema.helpers';

@Component({
  selector: 'big-query-source-editor',
  template: `
    <div>
      <xp-steps>
        <xp-step [step]="connectionStep">
          <xp-select-picker-editable
            id="connection-picker-component"
            [type]="selectPickerTypes.connection"
            [value]="rawComponent.connection"
            placeholder="Select connection"
            emptyPlaceholder="Connections list is empty"
            (valueChange)="onSelectConnection($event)"
            (createNew)="onCreateNewConnection($event)"
            [params]="{ type: component.connectionTypes }"
            [connectionTypes]="component.connectionTypes.split(',')"
          ></xp-select-picker-editable>
          <dynamic-connection [component]="rawComponent" *ngxPermissionsOnly="'dynamicConnection'"></dynamic-connection>
        </xp-step>
        <xp-step [step]="componentBaseStep" (activate)="onBaseStepActivation()">
          <div class="amazon-redshift-component">
            <xp-form-validation type="Xplenty::JobAuthoring::Components::BigQuerySourceComponent">
              <form name="componentForm" novalidate #form="ngForm">
                <div class="row">
                  <div class="col-md-12">
                    <database-definition [hasAccessMode]="true" [component]="component" [rawComponent]="rawComponent">
                    </database-definition>
                  </div>
                  <div class="col-md-6">
                    <div *ngIf="rawComponent.access_mode == 'query'">
                      <xp-input-checkbox
                        [ngModel]="rawComponent.use_legacy_sql"
                        (ngModelChange)="onValueChange($event, 'use_legacy_sql')"
                        [disabled]="isLegacySQLDisabled"
                        name="use_legacy_sql"
                        labelText="Use Legacy SQL"
                      ></xp-input-checkbox>
                    </div>
                    <div *ngIf="rawComponent.access_mode == 'query'">
                      <xp-input-checkbox
                        [ngModel]="rawComponent.stored_procedure"
                        (ngModelChange)="onValueChange($event, 'stored_procedure')"
                        name="stored_procedure"
                        [disabled]="isStoredProcedureDisabled"
                        labelText="Stored Procedure Query"
                      ></xp-input-checkbox>
                    </div>
                  </div>
                </div>
              </form>
            </xp-form-validation>
          </div>
        </xp-step>
        <xp-step [step]="schemaImporterStep" (activate)="activateSchemaStep()" [isWide]="true">
          <schema-importer
            (fieldsChange)="onFieldsChange($event)"
            [fields]="(rawComponent.schema || {}).fields || []"
            [component]="component"
            [rawComponent]="rawComponent"
            (updateValidation)="updateSchemaValidation($event)"
          ></schema-importer>
        </xp-step>
      </xp-steps>
    </div>
  `,
})
export class BigQuerySourceEditorComponent extends BaseForm implements BaseFormInterface, OnChanges {
  @Input() rawComponent: AnyComponentData;
  @Input() component: ComponentTypeItem;
  @Input() parentSchemas: Schema[];
  @Output() formValidationChange = new EventEmitter<boolean>();
  @Output() createConnection = new EventEmitter();
  @ViewChild('form') form: NgForm;
  formName = 'componentForm';
  successMessageText = '';

  selectPickerTypes = SelectPickerTypes;

  isSchemaValid = true;
  isFormValid = true;
  isBaseComponentStepActivated = false;
  validationChangeSubscription: Subscription;

  connectionStep: Step = getStep({ active: true });
  componentBaseStep: Step = getStep({});
  schemaImporterStep: Step = getStep({});

  isLegacySQLDisabled = false;
  isStoredProcedureDisabled = false;

  constructor(
    protected store: Store<AppState>,
    protected notify: NotifyService,
    protected translate: TranslateService,
    private componentFormTagsService: ComponentFormTagsService,
    @Inject('$stateParams') private $stateParams: any,
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.validationChangeSubscription = this.formValidationChange.subscribe((isFormValid) => {
      this.isFormValid = isFormValid;

      this.componentBaseStep = {
        ...this.componentBaseStep,
        valid: !!this.rawComponent.connection?.id && isFormValid,
        isError: this.isBaseComponentStepActivated && !isFormValid,
        tags: this.componentFormTagsService.getTags(this.rawComponent, this.component),
      };
      this.onValidityChange();
    });

    this.connectionStep = getStep({
      title: this.translate.instant(`component-editor.step-connection.${this.component.type}.closed`),
      activeTitle: this.translate.instant(`component-editor.step-connection.${this.component.type}.active`),
      valid: !!this.rawComponent.connection?.id,
      active: true,
    });

    this.componentBaseStep = getStep({
      title: this.translate.instant(`component-editor.step-editor.${this.component.componentType}.closed`),
      activeTitle: this.translate.instant(`component-editor.step-editor.${this.component.componentType}.active`),
      valid: !!this.rawComponent.connection?.id,
      tags: this.rawComponent.connection?.id
        ? this.componentFormTagsService.getTags(this.rawComponent, this.component)
        : [],
    });

    this.schemaImporterStep = getStep({
      title: this.translate.instant(`component-editor.step-schema.${this.component.componentType}.closed`),
      activeTitle: this.translate.instant(`component-editor.step-schema.${this.component.componentType}.active`),
      valid: !!this.rawComponent.schema?.fields?.length,
      tags: (((this.rawComponent.schema || {}).fields as ConnectionSchemaField[]) || []).map((field) => ({
        name: field.alias,
      })),
    });

    if (this.rawComponent.connection?.id) {
      this.store.dispatch(
        getConnectionSchema({
          connectionId: this.rawComponent.connection?.id,
          connectionType: this.rawComponent.connection?.type,
          schemaRequestData: getDataForSchemaImporter(
            this.rawComponent,
            this.component.componentType,
            this.$stateParams.account_id,
          ),
        }),
      );
    }

    if (this.rawComponent.stored_procedure) {
      this.isLegacySQLDisabled = true;
      this.onValueChange(false, 'use_legacy_sql');
    }

    if (this.rawComponent.use_legacy_sql) {
      this.isStoredProcedureDisabled = true;
      this.onValueChange(false, 'stored_procedure');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.componentBaseStep = {
      ...this.componentBaseStep,
      tags: this.rawComponent.connection?.id
        ? this.componentFormTagsService.getTags(this.rawComponent, this.component)
        : [],
    };

    if (
      changes.rawComponent?.currentValue?.dynamic_connection !== changes.rawComponent?.previousValue?.dynamic_connection
    ) {
      if (this.rawComponent.dynamic_connection) {
        if (!this.connectionStep.tags.find((tag) => tag.name === this.component.dynamic_connection)) {
          this.connectionStep.tags = [
            ...this.connectionStep.tags,
            {
              name: this.component.dynamic_connection,
            },
          ];
        }
      } else {
        this.connectionStep.tags = this.connectionStep.tags.filter(
          (tag) => tag.name !== changes.component.previousValue.dynamic_connection,
        );
      }
      this.connectionStep = { ...this.connectionStep };
    }
  }

  onBaseStepActivation() {
    this.isBaseComponentStepActivated = true;
  }

  updateSchemaValidation(isSchemaValid: boolean) {
    this.isSchemaValid = isSchemaValid;

    this.schemaImporterStep = { ...this.schemaImporterStep, valid: this.isSchemaValid, isError: !this.isSchemaValid };
    this.onValidityChange();
  }

  activateSchemaStep() {
    if (this.rawComponent.connection?.id) {
      this.store.dispatch(
        getConnectionSchema({
          connectionId: this.rawComponent.connection?.id,
          connectionType: this.rawComponent.connection?.type,
          schemaRequestData: getDataForSchemaImporter(
            this.rawComponent,
            this.component.componentType,
            this.$stateParams.account_id,
          ),
        }),
      );
    }
  }

  onValidityChange() {
    const isValid = this.isSchemaValid && this.isFormValid;

    this.store.dispatch(setComponentValidity({ isComponentFormValid: isValid }));
  }

  onSelectConnection(connection: AnyConnection) {
    this.store.dispatch(
      updateRawComponent({
        rawComponent: { connection },
      }),
    );
    this.store.dispatch(updateComponent({ component: { connection } }));

    const img = `<img class="tag-icon" src="${connectionIconUrlByType(connection.type)}" alt="${connection.name}" />`;

    this.connectionStep.tags = [
      {
        name: `${img}<b>${escape(connection.name)}</b>`,
      },
    ];

    if (this.rawComponent.dynamic_connection) {
      this.connectionStep.tags = [...this.connectionStep.tags, { name: this.rawComponent.dynamic_connection }];
    }

    this.connectionStep = { ...this.connectionStep, valid: true };
  }

  onCreateNewConnection(params) {
    this.createConnection.emit(params);
  }

  onValueChange(value: any, key: string) {
    if (this.rawComponent[key] === value) {
      return;
    }

    const newComponent: any = {
      [key]: value,
    };
    if (key === 'use_legacy_sql') {
      if (value) {
        this.isStoredProcedureDisabled = true;
        newComponent.stored_procedure = false;
      } else {
        this.isStoredProcedureDisabled = false;
      }
    }

    if (key === 'stored_procedure') {
      if (value) {
        this.isLegacySQLDisabled = true;
        newComponent.use_legacy_sql = false;
      } else {
        this.isLegacySQLDisabled = false;
      }
    }

    this.store.dispatch(
      updateRawComponent({
        rawComponent: { ...newComponent },
      }),
    );
    this.store.dispatch(updateComponent({ component: { ...newComponent } }));
  }

  onFieldsChange(fields: ConnectionSchemaField[]) {
    this.schemaImporterStep = {
      ...this.schemaImporterStep,
      tags: fields.map((field) => ({
        name: field.alias,
      })),
    };

    this.store.dispatch(
      updateRawComponent({
        rawComponent: { schema: { ...this.rawComponent.schema, fields } },
      }),
    );
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    if (this.validationChangeSubscription) {
      this.validationChangeSubscription.unsubscribe();
    }
  }
}
